import { useMemo, useState } from 'react'
import { Link, useMatch } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Archive,
  ArchiveRestore,
  Building2,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderTree,
  ListChecks,
  Plus,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import {
  departmentList,
  folderListResponse,
  listListResponse,
  spaceListResponse,
} from '../../lib/types'
import { ListRowActions } from '../ListRowActions'
import { FolderRowActions } from '../FolderRowActions'
import { SpaceRowActions } from '../SpaceRowActions'
import { SpaceCreateDialog } from '../SpaceCreateDialog'
import { SpaceShareDialog } from '../SpaceShareDialog'

/**
 * Inline workspace → spaces → lists/folders tree for the Admin
 * sidebar. Activates only when the admin is inside a Space (URL
 * matches `/app/spaces/:spaceId(/…)`); off that route it returns
 * null and the sidebar stays at the compact "Space Explorer" button.
 *
 * Mirrors the User-shell sidebar tree visually:
 *   • Every space in that workspace, each with chevron + avatar +
 *     name + hover-reveal 3-dot SpaceRowActions menu
 *   • Active space auto-expands and shows its folders + lists with
 *     a NavLink-style active highlight on the current list row
 *   • "+ New Space" CTA at the bottom for inline creation
 *
 * Dark sidebar palette throughout (white/* tones) so it sits on the
 * navy admin chrome without visual mismatch. No drag-drop — admins
 * reorder spaces via the row's 3-dot menu, not by dragging.
 */
export function AdminCurrentSpaceTree() {
  const match = useMatch('/app/spaces/:spaceId/*')
  const activeSpaceId = match?.params?.spaceId
  const listMatch = useMatch('/app/spaces/:spaceId/lists/:listId')
  const activeListId = listMatch?.params?.listId

  // 1) Fetch the active space to discover its workspaceId.
  const activeSpaceQuery = useQuery({
    queryKey: ['space', activeSpaceId, 'sidebar-tree'],
    enabled: Boolean(activeSpaceId),
    queryFn: async () => {
      const res = await api.get(`/spaces/${activeSpaceId}`)
      return res.data
    },
    staleTime: 60_000,
  })
  const activeSpace = activeSpaceQuery.data
  const workspaceId = activeSpace?.workspaceId

  // 2) Fetch every space in the workspace so the admin sees the
  //    full sibling set, not just the active space. Shares cache
  //    with SpacesModal. (The Workspace name itself is intentionally
  //    NOT shown — TaskMagix is single-Workspace, so a Workspace header
  //    in the sidebar tree is redundant noise.)
  const spacesQuery = useQuery({
    queryKey: ['spaces', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/spaces`)
      return spaceListResponse.parse(res.data)
    },
    staleTime: 30_000,
  })
  const rawSpaces = spacesQuery.data?.data ?? []
  // Personal ("My Space") spaces live ONLY in their own sidebar section
  // (<MySpaceNav />) — never in Space Explorer. Exclude them from the tree.
  const spaces = rawSpaces.filter((s) => !s.isArchived && !s.isPersonal)
  // Archived spaces (returned by the same endpoint) — surfaced in a
  // collapsible "Archived" group at the bottom of the tree with a Restore
  // action, mirroring the member sidebar so admins can bring one back.
  const archivedSpaces = rawSpaces.filter(
    (s) => s.isArchived && !s.isPersonal,
  )
  const qc = useQueryClient()
  const [archivedOpen, setArchivedOpen] = useState(false)

  // Departments — Spaces are grouped under their Department header so the
  // admin sees which Department each Space belongs to, and each group's
  // "+ New Space" stamps that Department (no org-wide spaces from here).
  const departmentsQuery = useQuery({
    queryKey: ['departments', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/departments`)
      return departmentList.parse(res.data)
    },
    staleTime: 30_000,
  })
  const departments = (departmentsQuery.data?.data ?? []).filter(
    (d) => d.status === 'active',
  )
  // Group active spaces by Department. Spaces with no Department (org-wide)
  // fall into `orgWideSpaces` — rendered as plain rows so they stay
  // reachable/deletable; the per-Department "+ New Space" never creates one.
  const spacesByDept = new Map()
  const orgWideSpaces = []
  for (const s of spaces) {
    if (s.departmentId) {
      const key = String(s.departmentId)
      if (!spacesByDept.has(key)) spacesByDept.set(key, [])
      spacesByDept.get(key).push(s)
    } else {
      orgWideSpaces.push(s)
    }
  }

  // Accordion behaviour — at most one space is expanded at a time.
  // The active space starts expanded so the user sees their lists on
  // first render; when the user navigates into a list under a
  // different space the new active space takes over the open slot
  // (the `lastActive` field tracks the previous activeSpaceId so we
  // can detect that change during render rather than from an effect).
  // Clicking the currently-open space's chevron closes it (sets to
  // null) — accordion-style, the user can collapse everything.
  //
  // React docs: this is the "adjusting state when a prop changes"
  // pattern — preferred over a useEffect because it avoids a
  // cascading second render after the prop update.
  //
  // BOTH sides of the comparison are normalised to `null` via `??`
  // because `activeSpaceId` is `undefined` on routes outside
  // `/app/spaces/...` (the sidebar tree mounts on every admin page
  // even when there's no active space). Without the normalisation
  // the equality check would be `null !== undefined === true` on
  // every render, triggering an unbounded setState → re-render
  // loop ("Too many re-renders").
  const normalisedActive = activeSpaceId ?? null
  const [openState, setOpenState] = useState({
    id: normalisedActive,
    lastActive: normalisedActive,
  })
  if (openState.lastActive !== normalisedActive) {
    setOpenState({ id: normalisedActive, lastActive: normalisedActive })
  }
  const openSpaceId = openState.id

  function isExpanded(spaceId) {
    return spaceId === openSpaceId
  }
  function toggleSpace(spaceId) {
    setOpenState((prev) => ({
      ...prev,
      id: prev.id === spaceId ? null : spaceId,
    }))
  }

  // The sidebar tree is scoped to JUST the active space's department —
  // the admin picks a department + space in the Space Explorer dialog, so
  // the sidebar then shows only that department and its spaces, never the
  // other departments. Switch departments via the dialog, not the tree.
  // The active department is read off the workspace spaces list (the same
  // source the grouping uses) so it's always present.
  const activeDept = spaces.find((s) => s.id === activeSpaceId)
  const activeDeptId = activeDept?.departmentId
    ? String(activeDept.departmentId)
    : null
  const visibleDepartments = activeDeptId
    ? departments.filter((d) => String(d.id) === activeDeptId)
    : []

  // The viewer's OWN personal Space must NEVER appear in Space Explorer — it
  // lives only in the dedicated "Personal Spaces" sidebar section. The ONLY
  // personal Space that belongs here is one the viewer does NOT own: a platform
  // admin viewing a company-wide member's personal Space (the accountability
  // exception), which the backend tags with `ownerName`. So gate the group on
  // `ownerName` — present ⇒ a foreign (admin-viewed) Space, show it; absent ⇒
  // the viewer's own, hide it (it was being duplicated into Space Explorer).
  const activePersonalSpaceRaw =
    rawSpaces.find((s) => s.id === activeSpaceId && s.isPersonal) ??
    (activeSpace?.isPersonal ? { ...activeSpace, id: activeSpaceId } : null)
  const activePersonalSpace = activePersonalSpaceRaw?.ownerName
    ? activePersonalSpaceRaw
    : null

  // Department a new Space is being created in (its id while the dialog is
  // open, null when closed) — set by a group's "+ New Space".
  const [createDeptId, setCreateDeptId] = useState(null)
  // Space whose Sharing & Permissions dialog is open (null = closed).
  const [shareTarget, setShareTarget] = useState(null)

  // Off a Space route, or before the active Space loads → render nothing.
  //
  // NOTE: being inside a PERSONAL Space no longer hides this tree. It used to
  // (a personal Space is navigated from the My Space section, and it is still
  // excluded from the tree's own listing above), but collapsing the whole
  // Space Explorer the moment you opened one left you with no way to navigate
  // back out to the company's Spaces — you had to leave the Space first. The
  // tree now stays put and simply highlights nothing, exactly as it does on a
  // Space that sits outside your active department.
  if (!activeSpaceId || !activeSpace || !workspaceId) return null

  return (
    <>
      <div className="mt-1 ml-3 pl-2 border-l border-white/10 space-y-0.5">
        {/* Spaces grouped by Department. Each Department header shows its
         *  name; its "+ New Space" creates a Space in that Department. */}
        {spacesQuery.isLoading || departmentsQuery.isLoading ? (
          <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">
            Loading spaces…
          </p>
        ) : visibleDepartments.length === 0 &&
          orgWideSpaces.length === 0 &&
          !activePersonalSpace ? (
          <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">
            No spaces to show.
          </p>
        ) : (
          <>
            {/* PERSONAL Space group — shown ONLY for a personal Space the viewer
             *  does NOT own (a platform admin viewing a company-wide member's
             *  personal Space, named by its owner). The viewer's OWN personal
             *  Space is excluded above so it never duplicates into Space Explorer;
             *  it lives solely in the "Personal Spaces" sidebar section. No
             *  "+ New Space" — you can't create inside a personal Space. */}
            {activePersonalSpace ? (
              <div className="mb-1">
                <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                  <FolderTree size={14} className="shrink-0 text-white/60" />
                  <span className="flex-1 truncate text-sm font-semibold text-white/80">
                    {activePersonalSpace.ownerName
                      ? `${activePersonalSpace.ownerName} — Personal Space`
                      : 'Personal Space'}
                  </span>
                </div>
                <SpaceTreeRow
                  space={activePersonalSpace}
                  workspaceId={workspaceId}
                  isActive
                  isExpanded={isExpanded(activePersonalSpace.id)}
                  onToggle={() => toggleSpace(activePersonalSpace.id)}
                  activeListId={activeListId}
                  onShare={() => setShareTarget(activePersonalSpace)}
                />
              </div>
            ) : null}
            {visibleDepartments.map((dept) => {
              const deptSpaces = spacesByDept.get(String(dept.id)) ?? []
              return (
                <div key={dept.id} className="mb-1">
                  {/* Department header — the single active department this
                   *  tree is scoped to. Plain (always shows its spaces)
                   *  since it's the only department here; switch departments
                   *  via the Space Explorer dialog. */}
                  <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                    <Building2 size={14} className="shrink-0 text-white/60" />
                    <span className="flex-1 truncate text-sm font-semibold text-white/80">
                      {dept.name}
                    </span>
                    <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/10 text-[10px] font-semibold tabular-nums text-white/60">
                      {deptSpaces.length}
                    </span>
                  </div>
                  {deptSpaces.length === 0 ? (
                    <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/25">
                      No spaces yet.
                    </p>
                  ) : (
                    deptSpaces.map((sp) => (
                      <SpaceTreeRow
                        key={sp.id}
                        space={sp}
                        workspaceId={workspaceId}
                        isActive={sp.id === activeSpaceId}
                        isExpanded={isExpanded(sp.id)}
                        onToggle={() => toggleSpace(sp.id)}
                        activeListId={activeListId}
                        onShare={() => setShareTarget(sp)}
                      />
                    ))
                  )}
                  {/* + New Space — creates in THIS Department (no picker;
                   *  the group header is the department context). */}
                  <button
                    type="button"
                    onClick={() => setCreateDeptId(String(dept.id))}
                    className="w-full flex items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-xs text-left cursor-pointer text-white/55 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <Plus size={13} strokeWidth={1.75} className="shrink-0" />
                    New Space
                  </button>
                </div>
              )
            })}
            {/* Org-wide spaces (no Department) — only when the active space
             *  itself is org-wide; otherwise the tree stays scoped to the
             *  active department. */}
            {!activeDeptId
              ? orgWideSpaces.map((sp) => (
                  <SpaceTreeRow
                    key={sp.id}
                    space={sp}
                    workspaceId={workspaceId}
                    isActive={sp.id === activeSpaceId}
                    isExpanded={isExpanded(sp.id)}
                    onToggle={() => toggleSpace(sp.id)}
                    activeListId={activeListId}
                    onShare={() => setShareTarget(sp)}
                  />
                ))
              : null}
          </>
        )}

        {/* Archived spaces — collapsible restore list. Mirrors the member
         *  sidebar's Archived group so an admin can bring a soft-deleted
         *  Space back (Restore flips isArchived → false). */}
        {archivedSpaces.length > 0 ? (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setArchivedOpen((v) => !v)}
              aria-expanded={archivedOpen}
              className="w-full flex items-center gap-2 rounded-[10px] px-3 py-1.5 text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 cursor-pointer transition-colors"
            >
              <ChevronDown
                size={11}
                className={
                  'shrink-0 transition-transform duration-150 ' +
                  (archivedOpen ? '' : '-rotate-90')
                }
              />
              {/* Group icon — same lucide Archive glyph as the space
               *  menu's Archive action and the member sidebar's group. */}
              <Archive size={11} className="shrink-0" />
              <span className="flex-1 text-left truncate">
                Archived ({archivedSpaces.length})
              </span>
            </button>
            {archivedOpen
              ? archivedSpaces.map((space) => (
                  <div
                    key={`archived-${space.id}`}
                    className="group/arch flex items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-sm text-white/55 opacity-80 hover:opacity-100 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    <SpaceAvatar name={space.name} color={space.color} size={20} />
                    <span className="flex-1 truncate">{space.name}</span>
                    <button
                      type="button"
                      aria-label={`Restore ${space.name}`}
                      title="Restore"
                      onClick={async () => {
                        try {
                          await api.patch(
                            `/workspaces/${workspaceId}/spaces/${space.id}`,
                            { isArchived: false },
                          )
                          toast.success('Space restored successfully.')
                          void qc.invalidateQueries({
                            queryKey: ['spaces', workspaceId],
                          })
                          void qc.invalidateQueries({
                            queryKey: ['space', space.id],
                          })
                        } catch (err) {
                          toast.error(extractApiError(err).message)
                        }
                      }}
                      className="p-1 rounded text-white/50 hover:text-white hover:bg-white/10 cursor-pointer opacity-0 group-hover/arch:opacity-100 transition-opacity"
                    >
                      <ArchiveRestore size={14} />
                    </button>
                  </div>
                ))
              : null}
          </div>
        ) : null}
      </div>

      <SpaceCreateDialog
        open={createDeptId !== null}
        workspaceId={workspaceId}
        departmentId={createDeptId ?? undefined}
        onClose={() => setCreateDeptId(null)}
      />
      <SpaceShareDialog
        open={Boolean(shareTarget)}
        spaceId={shareTarget?.id ?? null}
        workspaceId={workspaceId}
        spaceName={shareTarget?.name ?? ''}
        isPersonal={shareTarget?.isPersonal === true}
        onClose={() => setShareTarget(null)}
      />
    </>
  )
}

// ── Space row + its expanded children ────────────────────────────

export function SpaceTreeRow({
  space,
  workspaceId,
  isActive,
  isExpanded,
  onToggle,
  activeListId,
  onShare,
}) {
  return (
    <div>
      {/* `group/space relative` is scoped to the ROW only — the
       *  expanded children sit OUTSIDE this container so the
       *  absolute-positioned action cluster (positioned with
       *  `top-1/2`) anchors to the row's vertical centre rather
       *  than the centre of the whole expanded block. The earlier
       *  version wrapped both row + children in a single relative
       *  group, which floated the actions halfway down the tree
       *  when the space was expanded. */}
      <div className="group/space relative">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? `Collapse ${space.name}` : `Expand ${space.name}`
          }
          className={
            'w-full flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm text-left cursor-pointer transition-colors pr-14 ' +
            (isActive && !activeListId
              ? 'bg-white/10 text-white font-semibold'
              : 'text-white/75 hover:bg-white/5 hover:text-white')
          }
        >
          {isExpanded ? (
            <ChevronDown size={12} className="shrink-0 text-white/50" />
          ) : (
            <ChevronRight size={12} className="shrink-0 text-white/50" />
          )}
          <SpaceAvatar name={space.name} color={space.color} size={20} />
          <span className="flex-1 truncate" title={space.name}>
            {space.name}
          </span>
        </button>
        {/* Hover-reveal actions cluster — uses the production
         *  SpaceRowActions component so every menu item (Favourite
         *  / Rename / Move / Palette / Archive / Delete / etc.)
         *  works here exactly as it does in the user-shell sidebar. */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/space:opacity-100 transition-opacity">
          <SpaceRowActions
            space={space}
            workspaceId={workspaceId}
            onShare={onShare}
          />
        </div>
      </div>

      {/* Drilled content — lazy-fetched when the space expands.
       *  Sits OUTSIDE the relative container above so the actions
       *  cluster stays glued to the row. */}
      {isExpanded ? (
        <SpaceChildrenInline
          spaceId={space.id}
          workspaceId={workspaceId}
          activeListId={isActive ? activeListId : null}
        />
      ) : null}
    </div>
  )
}

// ── Lists + Folders inside an expanded space ─────────────────────

function SpaceChildrenInline({ spaceId, workspaceId, activeListId }) {
  // IMPORTANT: keep these query keys aligned with the rest of the
  // codebase — `['lists', spaceId]` and `['folders', spaceId]` are
  // what ListRowActions / ListCreateDialog / ListMoveDialog /
  // UserLayout / SpacePage all invalidate after every mutation. If
  // we used a bespoke key here (e.g. `['spaces', spaceId, 'lists']`)
  // the sidebar tree would silently miss every cache invalidation
  // and keep showing rows that no longer exist on the server, which
  // produces "List not found." 404s on the very next click.
  const listsQuery = useQuery({
    queryKey: ['lists', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/lists`)
      return listListResponse.parse(res.data)
    },
    staleTime: 30_000,
  })
  const foldersQuery = useQuery({
    queryKey: ['folders', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/folders`)
      return folderListResponse.parse(res.data)
    },
    staleTime: 30_000,
  })

  const partitioned = useMemo(() => {
    const lists = listsQuery.data?.data ?? []
    const byFolder = new Map()
    const root = []
    for (const ls of lists) {
      if (ls.folderId) {
        if (!byFolder.has(ls.folderId)) byFolder.set(ls.folderId, [])
        byFolder.get(ls.folderId).push(ls)
      } else {
        root.push(ls)
      }
    }
    return { byFolder, root }
  }, [listsQuery.data])

  const folders = foldersQuery.data?.data ?? []
  const lists = listsQuery.data?.data ?? []
  const loading = listsQuery.isLoading || foldersQuery.isLoading

  // Folder ACCORDION — at most one folder open, defaulting to the one
  // that holds the active list. Every folder used to start expanded,
  // which unfolded the entire space (every project + every list) the
  // moment the admin entered it (QA: "only the selected one should be
  // open, not all"). Mirrors the member sidebar's single-open-folder
  // behaviour and this file's own space-level accordion, including the
  // render-time state-adjust pattern (see that comment for why not a
  // useEffect) and the `?? null` normalisation on both sides.
  const activeFolderId =
    lists.find((l) => l.id === activeListId)?.folderId ?? null
  const [folderState, setFolderState] = useState({
    id: activeFolderId,
    lastActive: activeFolderId,
  })
  if (folderState.lastActive !== activeFolderId) {
    setFolderState({ id: activeFolderId, lastActive: activeFolderId })
  }
  const expandedFolderId = folderState.id

  return (
    <div className="ml-4 pl-2 border-l border-white/10 space-y-0.5 mt-0.5 mb-0.5">
      {loading ? (
        <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">
          Loading lists…
        </p>
      ) : folders.length === 0 && lists.length === 0 ? (
        <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">
          No lists yet.
        </p>
      ) : (
        <>
          {folders.map((f) => (
            <FolderBlock
              key={f.id}
              folder={f}
              lists={partitioned.byFolder.get(f.id) ?? []}
              spaceId={spaceId}
              workspaceId={workspaceId}
              activeListId={activeListId}
              collapsed={f.id !== expandedFolderId}
              onToggle={() =>
                setFolderState((prev) => ({
                  ...prev,
                  id: prev.id === f.id ? null : f.id,
                }))
              }
            />
          ))}
          {partitioned.root.map((ls) => (
            <ListLink
              key={ls.id}
              list={ls}
              spaceId={spaceId}
              workspaceId={workspaceId}
              active={ls.id === activeListId}
            />
          ))}
        </>
      )}
    </div>
  )
}

function FolderBlock({
  folder,
  lists,
  spaceId,
  workspaceId,
  activeListId,
  collapsed,
  onToggle,
}) {
  // Controlled by the parent's folder accordion (single open slot) —
  // this block previously owned a `useState(false)` that started
  // every folder expanded.
  return (
    <div>
      <div className="group/folder relative">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          title={folder.name}
          // pr-10 reserves room for the hover-reveal FolderRowActions kebab
          // (absolute, right-2) so the truncated name never runs under it —
          // matching the List rows.
          className="w-full flex items-center gap-2 px-3 py-1 pr-10 rounded-md text-left hover:bg-white/5 cursor-pointer"
        >
          {collapsed ? (
            <ChevronRight size={12} className="shrink-0 text-white/40" />
          ) : (
            <ChevronDown size={12} className="shrink-0 text-white/40" />
          )}
          <Folder size={14} className="shrink-0 text-white" />
          <span className="flex-1 truncate text-sm font-semibold text-white">
            {folder.name}
          </span>
        </button>
        {/* Hover-reveal 3-dot menu (Rename / Move / Delete) — same
         *  FolderRowActions the member sidebar uses, so projects have the
         *  same actions dropdown in both shells. */}
        {workspaceId ? (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/folder:opacity-100 transition-opacity">
            <FolderRowActions folder={folder} workspaceId={workspaceId} />
          </div>
        ) : null}
      </div>
      {!collapsed && lists.length > 0 ? (
        <div className="ml-3 pl-2 border-l border-white/10 space-y-0.5">
          {lists.map((ls) => (
            <ListLink
              key={ls.id}
              list={ls}
              spaceId={spaceId}
              workspaceId={workspaceId}
              active={ls.id === activeListId}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function ListLink({ list, spaceId, workspaceId, active }) {
  return (
    <div className="group/list relative">
      <Link
        to={`/app/spaces/${spaceId}/lists/${list.id}`}
        title={list.name}
        className={
          'flex items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-sm transition-colors pr-10 ' +
          (active
            ? 'bg-white/10 text-white font-semibold border border-white/15'
            : 'text-white/65 hover:bg-white/5 hover:text-white border border-transparent')
        }
      >
        <ListChecks size={13} className="shrink-0 text-white/40" />
        <span className="flex-1 truncate">{list.name}</span>
      </Link>
      {/* Hover-reveal 3-dot menu — uses the production
       *  ListRowActions component (Rename / Move / Privacy /
       *  Delete) so every action lands here exactly as it does in
       *  the user-shell sidebar. Sits absolute-positioned at the
       *  row's right edge with `top-1/2` for vertical centring;
       *  the parent `group/list relative` is row-scoped so this
       *  never floats away from its row. */}
      {workspaceId ? (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/list:opacity-100 transition-opacity">
          <ListRowActions list={list} workspaceId={workspaceId} />
        </div>
      ) : null}
    </div>
  )
}

/** Coloured letter avatar — same recipe used everywhere a Space
 *  avatar is rendered in the sidebar trees. White text on a tinted
 *  square so the avatar reads at any size. */
function SpaceAvatar({ name, color, size = 20 }) {
  const letter = (String(name ?? '?')[0] || '?').toUpperCase()
  const fontSize = Math.round(size * 0.5)
  return (
    <span
      className="inline-grid place-items-center rounded-md text-white font-semibold shrink-0"
      style={{
        background: color || '#02367B',
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${fontSize}px`,
      }}
      aria-hidden="true"
    >
      {letter}
    </span>
  )
}
